<script lang="ts">
  import { onMount } from 'svelte';
  import { Issue } from '../models/state';
  import RichTextEditor from './RichTextEditor.svelte';
  import { appStore } from '../stores/appStore';
  import { AppApiClient } from '../api_contracts/app.client';
  import { UserProfile } from '../api_contracts/app';

  let { issue, repositoryName, onClose, ownerId }: { issue: Issue; repositoryName: string; onClose: () => void; ownerId: string } = $props();

  // Store
  let client: AppApiClient | null = $state(null);
  let token: string = $state('');
  let profile: UserProfile | null = $state(null);

  appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
    profile = state.profile;
  });

  let newComment = $state('');
  let isEditing = $state(false);
  let editedTitle = $state(issue.title);
  let editedDescription = $state('');

  // Assignees
  let assignees: any[] = $state([]);
  let members: any[] = $state([]);
  let loadingMembers = $state(true);
  let assigningYourself = $state(false);

  // Mock comments data
  const comments = [
    {
      id: 1,
      author: issue.author,
      avatar: `https://ui-avatars.com/api/?name=${issue.author}&background=random`,
      content: `<p>This is the initial description of the issue.</p>`,
      createdAt: issue.createdAt,
      isAuthor: true,
    }
  ];

  function closeIssue() {
    console.log('Closing issue');
    // Backend API call will go here
  }

  function reopenIssue() {
    console.log('Reopening issue');
    // Backend API call will go here
  }

  function submitComment() {
    if (!newComment.trim()) return;
    console.log('New comment:', newComment);
    newComment = '';
    // Backend API call will go here
  }

  function saveEdit() {
    console.log('Saving edit:', editedTitle);
    isEditing = false;
    // Backend API call will go here
  }

  async function fetchMembers() {
    if (!client || !token) return;

    try {
      loadingMembers = true;

      const result = await client.getRepositoryMembers({
        token,
        ownerId,
        repositoryName,
      });

      if (result.response.successFull) {
        members = result.response.members || [];
      }
    } catch (e) {
      console.error('Error fetching members:', e);
    } finally {
      loadingMembers = false;
    }
  }

  async function assignYourself() {
    if (!client || !token || !profile || !profile.id) return;

    // Check if already assigned
    if (assignees.some(a => a.userId === profile.id)) {
      return;
    }

    try {
      assigningYourself = true;

      // TODO: Call backend API to assign yourself to this issue
      // For now, just add to local state
      const currentUser = members.find(m => m.userId === profile.id);
      if (currentUser) {
        assignees = [...assignees, currentUser];
      }

      console.log('Assigned yourself to issue');
    } catch (e) {
      console.error('Error assigning yourself:', e);
    } finally {
      assigningYourself = false;
    }
  }

  onMount(() => {
    fetchMembers();
    // Initialize assignees from issue data if available
    // assignees = issue.assignees || [];
  });
</script>

<div class="bg-white dark:bg-gray-800 min-h-screen">
  <div class="max-w-6xl mx-auto p-6">
    <!-- Back Button -->
    <button
      onclick={onClose}
      class="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
      </svg>
      Back to issues
    </button>

    <div class="flex flex-col md:flex-row gap-8">
      <!-- Main Content -->
      <div class="flex-1">
        <!-- Header -->
        <div class="mb-6">
          <div class="flex items-start gap-3 mb-4">
            {#if isEditing}
              <input
                type="text"
                bind:value={editedTitle}
                class="flex-1 text-2xl font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded px-3 py-2"
              />
            {:else}
              <h1 class="flex-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
                {issue.title}
                <span class="text-gray-500 dark:text-gray-400 font-normal">#{issue.number}</span>
              </h1>
            {/if}
            {#if !isEditing}
              <button
                onclick={() => isEditing = true}
                class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Edit
              </button>
            {:else}
              <div class="flex gap-2">
                <button
                  onclick={saveEdit}
                  class="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onclick={() => isEditing = false}
                  class="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            {/if}
          </div>

          <!-- Status and Meta -->
          <div class="flex items-center gap-4 text-sm">
            <div class="flex items-center gap-2">
              {#if issue.state === 'open'}
                <div class="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                  </svg>
                  <span class="font-medium">Open</span>
                </div>
              {:else}
                <div class="flex items-center gap-2 px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <span class="font-medium">Closed</span>
                </div>
              {/if}
            </div>
            <span class="text-gray-600 dark:text-gray-400">
              <span class="font-medium">{issue.author}</span> opened this issue {issue.createdAt}
            </span>
            <span class="text-gray-600 dark:text-gray-400">· {issue.comments} comments</span>
          </div>

          <!-- Labels -->
          {#if issue.labels.length > 0}
            <div class="mt-4 flex flex-wrap gap-2">
              {#each issue.labels as label}
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style="background-color: {label.color}20; color: {label.color}; border: 1px solid {label.color}40;"
                >
                  {label.name}
                </span>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Comments Section -->
        <div class="space-y-4">
          {#each comments as comment}
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div class="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                <img src={comment.avatar} alt={comment.author} class="w-8 h-8 rounded-full"/>
                <div class="flex-1">
                  <span class="font-medium text-gray-900 dark:text-gray-100">{comment.author}</span>
                  {#if comment.isAuthor}
                    <span class="ml-2 px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded">Author</span>
                  {/if}
                  <span class="ml-2 text-sm text-gray-600 dark:text-gray-400">commented {comment.createdAt}</span>
                </div>
              </div>
              <div class="px-4 py-4 prose dark:prose-invert max-w-none">
                {@html comment.content}
              </div>
            </div>
          {/each}
        </div>

        <!-- New Comment -->
        <div class="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div class="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
            <span class="font-medium text-gray-900 dark:text-gray-100">Add a comment</span>
          </div>
          <div class="p-4">
            <RichTextEditor bind:value={newComment} placeholder="Leave a comment..." minHeight="150px"/>
            <div class="mt-4 flex justify-between items-center">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Styling with Markdown is supported
              </div>
              <div class="flex gap-2">
                {#if issue.state === 'open'}
                  <button
                    onclick={closeIssue}
                    class="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Close issue
                  </button>
                {:else}
                  <button
                    onclick={reopenIssue}
                    class="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Reopen issue
                  </button>
                {/if}
                <button
                  onclick={submitComment}
                  disabled={!newComment.trim()}
                  class="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="w-full md:w-64 space-y-4">
        <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Assignees</h3>
          {#if assignees.length === 0}
            <div class="text-sm text-gray-600 dark:text-gray-400 mb-2">No one assigned</div>
          {:else}
            <div class="space-y-2 mb-3">
              {#each assignees as assignee}
                <div class="flex items-center gap-2">
                  {#if assignee.avatar}
                    <img src={assignee.avatar} alt={assignee.username} class="w-6 h-6 rounded-full" />
                  {:else}
                    <div class="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold">
                      {assignee.username.substring(0, 1).toUpperCase()}
                    </div>
                  {/if}
                  <span class="text-sm text-gray-900 dark:text-gray-100">{assignee.username}</span>
                </div>
              {/each}
            </div>
          {/if}
          {#if profile && !assignees.some(a => a.userId === profile.id)}
            <button
              onclick={assignYourself}
              disabled={assigningYourself || loadingMembers}
              class="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assigningYourself ? 'Assigning...' : 'Assign yourself'}
            </button>
          {/if}
        </div>

        <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Labels</h3>
          {#if issue.labels.length > 0}
            <div class="flex flex-wrap gap-1">
              {#each issue.labels as label}
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style="background-color: {label.color}20; color: {label.color}; border: 1px solid {label.color}40;"
                >
                  {label.name}
                </span>
              {/each}
            </div>
          {:else}
            <div class="text-sm text-gray-600 dark:text-gray-400">None yet</div>
          {/if}
        </div>

        <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Milestone</h3>
          <div class="text-sm text-gray-600 dark:text-gray-400">No milestone</div>
        </div>

        <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Development</h3>
          <div class="text-sm text-gray-600 dark:text-gray-400">No branches or pull requests</div>
        </div>
      </div>
    </div>
  </div>
</div>
